import React from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import {
  swapToAnswerOptions,
  swapToAnswerSA,
  updateAnswerKeyOptions,
  updateAnswerKeySA,
  updateQuestion,
} from '@/lib/slices/quiz-slice';

import Button from '@/common/components/ui/Button';
import {
  AnswerData,
  MCAnswerData,
  QuizQuestionData,
  QuizQuestionType,
  ShortAnswerData,
} from 'quiz-types';
import { CloseIcon } from '@/assets/icons/button';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputSelect from '@/common/components/input/StandardInputSelect';
import StandardInputCheckbox from '@/common/components/input/StandardInputCheckbox';
import StandardInputTextarea from '@/common/components/input/StandardInputTextarea';

interface QuizQuestionBuilderProps {
  index: number;
}

function QuizQuestionBuilder({ index }: QuizQuestionBuilderProps): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const questions: Array<QuizQuestionData> = useAppSelector(() => store.getState().quiz.questions);
  const answers: Array<AnswerData> = useAppSelector(() => store.getState().quiz.answers);
  const question = questions[index];

  const questionTypeOptions = [
    { value: 'Multiple Choice', label: 'Multiple Choice' },
    { value: 'Short Answer', label: 'Short Answer' },
    { value: 'Paragraph', label: 'Paragraph' },
  ];

  const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const updatedQuestion: Partial<QuizQuestionData> = { ...question, question: value };
    dispatch(updateQuestion({ index, question: updatedQuestion }));
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value;
    const updatedQuestion: Partial<QuizQuestionData> = { ...question };
    updatedQuestion.type = value as QuizQuestionType;
    if (value === 'Multiple Choice') {
      updatedQuestion.options = [{ index: 0, value: '' }];
      dispatch(swapToAnswerOptions(index));
    } else {
      updatedQuestion.options = [];
      dispatch(swapToAnswerSA(index));
    }
    dispatch(updateQuestion({ index, question: updatedQuestion }));
  };

  const handleOptionChange = (
    optIndex: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newOptions = question.options?.map((option, i) =>
      i === optIndex ? { ...option, value: event.target.value } : option,
    );
    const updatedQuestion = { ...question, options: newOptions };
    dispatch(updateQuestion({ index, question: updatedQuestion }));
  };

  const handleCorrectOptionChange = (
    optIndex: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    // Pulls existing entry for question if exists or makes a new entry
    const newAnswerData: AnswerData = answers.some((entry) => entry.questionIndex == question.index)
      ? answers.filter((answer) => answer.questionIndex == question.index)[0]
      : ({ questionIndex: question.index, answerData: [] } as AnswerData);

    // Add or remove option from current answer array
    let newAnswerArray = [...(newAnswerData.answerData as Array<number>)];

    if (newAnswerArray.includes(optIndex)) {
      newAnswerArray = newAnswerArray.filter((option) => option !== optIndex);
    } else {
      newAnswerArray.push(optIndex);
    }

    dispatch(
      updateAnswerKeyOptions({
        index: question.index,
        answer: { ...newAnswerData, answerData: newAnswerArray },
      }),
    );
  };

  const addOption = (): void => {
    let newOptionIndex = 0;
    if (question.options) {
      newOptionIndex = question.options.length;
    }

    const newOptions = [...(question.options || []), { index: newOptionIndex, value: '' }];
    const updatedQuestion = { ...question, options: newOptions };
    dispatch(updateQuestion({ index, question: updatedQuestion }));
  };

  const deleteOption = (optIndex: number): void => {
    if (!question.options) {
      return;
    }

    let newOptions = [...question.options];
    newOptions.splice(optIndex, 1);
    newOptions = newOptions.map((option, index) => ({ index: index, value: option.value }));

    const updatedQuestion = { ...question, options: newOptions };
    dispatch(updateQuestion({ index, question: updatedQuestion }));
  };

  const handleAnswerChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    dispatch(updateAnswerKeySA({ index: index, answer: event.target.value }));
  };

  const getShortAnswerData = (): string => {
    for (const answer of answers) {
      if (answer.questionIndex === index) {
        const data = answer.answerData as ShortAnswerData;
        return data;
      }
    }
    return '';
  };

  const isChecked = (optionIndex: number): boolean | undefined => {
    for (const answer of answers) {
      if (answer.questionIndex === index) {
        const data = answer.answerData as MCAnswerData;
        return data.includes(optionIndex);
      }
    }
    return false;
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex gap-2 flex-col sm:flex-row'>
        <StandardInputText
          id={`question-${index}`}
          label='Question'
          value={question.question}
          onChange={handleQuestionChange}
          placeholderText='Quiz Question'
          isRequired
        />
        <div className='w-full sm:w-1/3'>
          <StandardInputSelect
            id={`question-type-${index}`}
            label={'Type'}
            options={questionTypeOptions}
            value={question.type}
            onChange={handleTypeChange}
          />
        </div>
      </div>
      {question.type === 'Multiple Choice' && (
        <div className=''>
          {question.options?.map((option, optIndex) => (
            <div key={optIndex} className='flex items-center mb-2 gap-2'>
              <StandardInputText
                id={`option-${index}-${optIndex}`}
                label={`Option ${optIndex + 1}`}
                value={option.value}
                onChange={(event) => handleOptionChange(optIndex, event)}
                placeholderText={`Option ${optIndex + 1}`}
                isRequired
              />
              <Button
                text={true}
                onClick={() => deleteOption(optIndex)}
                className='w-5 h-5 text-text_gray_400 mt-8'
              >
                <CloseIcon />
              </Button>
              <div className='mt-8'>
                <StandardInputCheckbox
                  id={`correct-${index}-${optIndex}`}
                  label='Correct?'
                  checked={isChecked(optIndex) ?? false}
                  onChange={(event) => handleCorrectOptionChange(optIndex, event)}
                />
              </div>
            </div>
          ))}
          <Button text={true} onClick={addOption} className='text-blue-500 hover:underline mt-2'>
            + Add option
          </Button>
        </div>
      )}
      {question.type === 'Short Answer' && (
        <StandardInputText
          id={`short-answer-${index}`}
          label='Short Answer'
          value={getShortAnswerData()}
          onChange={handleAnswerChange}
          placeholderText='Optional: add an answer'
        />
      )}
      {question.type === 'Paragraph' && (
        <StandardInputTextarea
          id={`paragraph-answer-${index}`}
          label='Long Answer'
          value={getShortAnswerData()}
          onChange={handleAnswerChange}
          placeholderText='Long answer text'
          rows={5}
        />
      )}
    </div>
  );
}

export default QuizQuestionBuilder;
